import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatPrefix
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import {
  BehaviorSubject,
  Observable,
  map,
  mergeMap,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs';
import { AddCurrencyPrefixDirective } from '../../directives/add-currency-prefix.directive';
import { KeyValuePairComponent } from '../../general/key-value-pair/key-value-pair.component';
import { HlCurrencyPipe } from '../../pipes/hl-currency.pipe';
import { PriceStructure, ProductService } from '../../services/product.service';
import {
  HIREABLE_PRICE_UPDATE_IJ,
  HireablePriceUpdate
} from './hireable-price-update';
import { NgClass } from '@angular/common';
import { TestIdDirective } from '../../directives/test-id.directive';
import { HIRE_PRICE_STRUCTURE_DIALOG_TEST_IDS } from './testIds';
import { PriceSuggestion } from 'src/app/models/product-hire-estimate';

export type PriceStructureDialogResponse = {
  pricing_type: 'default' | 'fixed' | 'custom' | 'no_charge';
  free_accessory: boolean;
  zero_price_reason: string;
  charge: number | null;
};

export type HirePriceStructureDialogData = {
  pricingType: 'default' | 'fixed' | 'custom' | 'no_charge';
  fixedPrice: number | null;
  free_accessory: boolean;
  zero_price_reason: string;
  currency: string;
  itemIsOffHired: boolean;
  productId: number;
  parentId: number;
  quantity: number;
  onHire: string;
  offHire: string;
  currencyRate: number;
  customerId: number;
  hireableId: number;
  hireableType: string;
};

@Component({
  selector: 'app-hire-price-structure-dialog',
  templateUrl: './hire-price-structure-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductService],
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    ReactiveFormsModule,
    KeyValuePairComponent,
    MatFormField,
    MatLabel,
    MatIcon,
    MatPrefix,
    AddCurrencyPrefixDirective,
    MatInput,
    MatCheckbox,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    AsyncPipe,
    HlCurrencyPipe,
    MatRadioGroup,
    MatRadioButton,
    MatError,
    MatProgressSpinner,
    MatIcon,
    NgClass,
    TestIdDirective
  ]
})
export class HirePriceStructureDialogComponent implements OnInit {
  default$: Observable<Partial<PriceStructure>>;
  customer$: Observable<Partial<PriceStructure>>;
  hasCustomerPrice$: Observable<boolean>;
  hasHireSpecificPrice$: Observable<boolean>;

  // Loading states for price observables
  defaultLoading$ = new BehaviorSubject<boolean>(true);
  customerLoading$ = new BehaviorSubject<boolean>(true);
  hireSpecificLoading$ = new BehaviorSubject<boolean>(true);

  priceFields = [
    { name: 'first_day', label: 'First Day', required: true },
    { name: 'add_day', label: 'Add Day', required: true },
    { name: 'week', label: 'Week', required: true },
    { name: 'weekend', label: 'Weekend' },
    { name: 'month', label: 'Month' }
  ];

  // Main form only tracks pricingType
  form = new FormGroup({
    pricingType: new FormControl<'default' | 'fixed' | 'custom' | 'no_charge'>(
      'default',
      Validators.required
    )
  });

  // Sub-forms for each pricing type
  fixedForm = new FormGroup({
    fixedPrice: new FormControl(null, [
      Validators.required,
      Validators.min(0.01)
    ])
  });

  customForm = new FormGroup({
    first_day: new FormControl(null, Validators.required),
    add_day: new FormControl(null, Validators.required),
    week: new FormControl(null, Validators.required),
    weekend: new FormControl(null),
    month: new FormControl(null),
    save_to_customer: new FormControl(false),
    currency: new FormControl(null)
  });

  noChargeForm = new FormGroup({
    free_accessory: new FormControl(false),
    no_charge_reason: new FormControl('', [Validators.required])
  });

  saving = new BehaviorSubject<boolean>(false);
  saving$ = this.saving.asObservable();
  itemIsOffHired = false;

  saveDisabled$ = this.saving
    .asObservable()
    .pipe(
      withLatestFrom(
        this.form.statusChanges.pipe(map((status) => status === 'INVALID'))
      )
    );

  public testIds = HIRE_PRICE_STRUCTURE_DIALOG_TEST_IDS;

  constructor(
    private productService: ProductService,
    @Inject(MAT_DIALOG_DATA) public data: HirePriceStructureDialogData,
    @Inject(HIREABLE_PRICE_UPDATE_IJ)
    public hireablePriceUpdateService: HireablePriceUpdate,
    private dialogRef: MatDialogRef<
      HirePriceStructureDialogComponent,
      PriceStructureDialogResponse
    >
  ) {
    this.customForm.get('currency').patchValue(data.currency);
    this.itemIsOffHired = data.itemIsOffHired === true;
    this.default$ = this.productService
      .getConfiguredPrices(this.data.productId, null, data.currency)
      .pipe(
        map((prices) => this.filterUndefined(prices)),
        tap({
          next: () => this.defaultLoading$.next(false),
          error: () => this.defaultLoading$.next(false)
        })
      );
    this.customer$ = this.productService
      .getConfiguredPrices(
        this.data.productId,
        this.data.customerId,
        data.currency
      )
      .pipe(
        map((prices) => this.filterUndefined(prices)),
        tap({
          next: () => this.customerLoading$.next(false),
          error: () => this.customerLoading$.next(false)
        }),
        shareReplay(1)
      );

    this.hasCustomerPrice$ = this.customer$.pipe(
      map((prices) => Object.keys(prices).length > 0)
    );
    this.hasHireSpecificPrice$ = this.productService
      .getConfiguredPrices(
        this.data.productId,
        this.data.customerId,
        data.currency,
        data.hireableId,
        data.hireableType
      )
      .pipe(
        map((prices) => this.filterUndefined(prices)),
        tap((prices: { [key: string]: number }) => {
          // Patch the form with these prices, they will be displayed in the form
          const decimalPrices = Object.entries(prices).reduce(
            (acc, [key, value]) => {
              acc[key] = value ? value / 100 : null;
              return acc;
            },
            {}
          );
          this.customForm.patchValue(decimalPrices);
        }),
        map((prices) => Object.keys(prices).length > 0),
        tap({
          next: () => this.hireSpecificLoading$.next(false),
          error: () => this.hireSpecificLoading$.next(false)
        }),
        tap((hasHireSpecificPrice) => {
          // if the hire has specific prices then set the pricing type to custom
          if (hasHireSpecificPrice) {
            this.form.get('pricingType').setValue('custom');
          }
        }),
        shareReplay(1)
      );
  }

  ngOnInit() {
    if (this.data && this.data.pricingType) {
      this.form.get('pricingType').setValue(this.data.pricingType);
    }
    if (this.data && this.data.fixedPrice != null) {
      this.fixedForm
        .get('fixedPrice')
        .setValue(
          (this.data.fixedPrice * (this.data?.currencyRate ?? 1)).toFixed(2)
        );
    }
    if (this.data && this.data.free_accessory != null) {
      this.noChargeForm
        .get('free_accessory')
        .setValue(Boolean(this.data.free_accessory));
      this.checkIfZeroPriceReasonShouldBeRequired(
        Boolean(this.data.free_accessory)
      );
    }
    if (this.data && this.data.zero_price_reason != null) {
      this.noChargeForm
        .get('no_charge_reason')
        .setValue(this.data.zero_price_reason);
    }
    // Add conditional validator for no_charge_reason
    this.noChargeForm.get('free_accessory').valueChanges.subscribe((isFree) => {
      this.checkIfZeroPriceReasonShouldBeRequired(isFree);
    });
    // Patch currency for customForm
    if (this.data && this.data.currency) {
      this.customForm.get('currency').patchValue(this.data.currency);
    }
  }

  private checkIfZeroPriceReasonShouldBeRequired(isFree: boolean) {
    const reasonCtrl = this.noChargeForm.get('no_charge_reason');
    if (isFree) {
      reasonCtrl.clearValidators();
    } else {
      reasonCtrl.setValidators([Validators.required]);
    }
    reasonCtrl.updateValueAndValidity();
  }

  get pricingType(): 'default' | 'fixed' | 'custom' | 'no_charge' {
    return this.form.get('pricingType').value;
  }
  set pricingType(val) {
    this.form.get('pricingType').setValue(val);
  }
  get fixedPrice() {
    return this.fixedForm.get('fixedPrice').value;
  }
  set fixedPrice(val) {
    this.fixedForm.get('fixedPrice').setValue(val);
  }

  save() {
    const pricingType = this.form.get('pricingType').value;
    let valid = true;
    switch (pricingType) {
      case 'fixed':
        this.fixedForm.markAllAsTouched();
        valid = this.fixedForm.valid;
        break;
      case 'custom':
        this.customForm.markAllAsTouched();
        valid = this.customForm.valid;
        break;
      case 'no_charge':
        this.noChargeForm.markAllAsTouched();
        valid = this.noChargeForm.valid;
        break;
      default:
        break;
    }
    if (!valid) return;
    this.saving.next(true);
    switch (pricingType) {
      case 'no_charge':
        this.handleNoChargeSave();
        break;
      case 'custom':
        this.handleCustomSave();
        break;
      case 'fixed':
        this.handleFixedSave();
        break;
      default:
        this.handleDefaultSave();
        break;
    }
  }

  private handleNoChargeSave() {
    if (
      !this.noChargeForm.get('free_accessory').value &&
      !this.noChargeForm.get('no_charge_reason').value
    ) {
      this.noChargeForm.get('no_charge_reason').setErrors({ required: true });
      this.saving.next(false);
      return;
    }
    this.hireablePriceUpdateService
      .update(
        this.data.parentId,
        this.data.hireableId,
        0, // zero price
        'no_charge',
        null,
        this.noChargeForm.get('no_charge_reason').value,
        this.noChargeForm.get('free_accessory').value
      )
      .subscribe({
        next: () => {
          this.saving.next(false);
          this.dialogRef.close({
            pricing_type: 'no_charge',
            free_accessory: this.noChargeForm.get('free_accessory').value,
            zero_price_reason: this.noChargeForm.get('no_charge_reason').value,
            charge: 0
          });
        },
        error: (err) => {
          this.saving.next(false);
          console.error('Failed to save no charge', err);
        }
      });
  }

  private handleCustomSave() {
    const payload: any = {
      product_id: this.data.productId,
      customer_id: this.data.customerId,
      currency_code: this.customForm.get('currency').value,
      hireable_id: this.data.hireableId,
      hireable_type: this.data.hireableType,
      prices: this.getPricesFromCustomForm(),
      save_to_customer: this.customForm.get('save_to_customer').value
    };
    this.productService
      .addCustomPrice(payload)
      .pipe(switchMap(() => this.getPriceAndUpdate()))
      .subscribe({
        next: () => {
          this.saving.next(false);
          this.dialogRef.close({
            pricing_type: 'custom',
            charge: null,
            free_accessory: null,
            zero_price_reason: null
          });
        },
        error: (err) => {
          this.saving.next(false);
          console.error('Failed to save custom price', err);
        }
      });
  }

  private handleFixedSave() {
    this.hasHireSpecificPrice$
      .pipe(
        take(1),
        switchMap((hasCustom) => {
          if (hasCustom) {
            return this.deleteHireSpecificPrice();
          }
          return of(null);
        }),
        switchMap(() => this.getPriceAndUpdate())
      )
      .subscribe({
        next: () => {
          this.saving.next(false);
          this.dialogRef.close({
            pricing_type: 'fixed',
            charge: this.fixedForm.get('fixedPrice').value,
            free_accessory: null,
            zero_price_reason: null
          });
        },
        error: (err) => {
          this.saving.next(false);
          console.error('Failed to save fixed price', err);
        }
      });
  }

  private handleDefaultSave() {
    this.hasHireSpecificPrice$
      .pipe(
        take(1),
        switchMap((hasCustom) => {
          if (hasCustom) {
            return this.deleteHireSpecificPrice();
          }
          return of(null);
        }),
        switchMap(() => this.getPriceAndUpdate())
      )
      .subscribe({
        next: () => {
          this.saving.next(false);
          this.dialogRef.close({
            pricing_type: this.pricingType,
            charge: null,
            free_accessory: null,
            zero_price_reason: null
          });
        },
        error: (err) => {
          this.saving.next(false);
          console.error('Failed to update price', err);
        }
      });
  }

  private deleteHireSpecificPrice() {
    return this.productService.deleteCustomPrice(
      this.data.productId,
      this.data.customerId,
      this.data.hireableId,
      this.data.hireableType
    );
  }

  private getPriceAndUpdate(): Observable<any> {
    if (this.pricingType === 'fixed') {
      const fixedPriceWithCurrencyFactor =
        this.fixedPrice / (this.data?.currencyRate ?? 1);
      return this.hireablePriceUpdateService.update(
        this.data.parentId,
        this.data.hireableId,
        fixedPriceWithCurrencyFactor,
        this.pricingType
      );
    }
    if (!this.itemIsOffHired) {
      return this.hireablePriceUpdateService.update(
        this.data.parentId,
        this.data.hireableId,
        null,
        this.pricingType
      );
    }
    return this.productService
      .getPrice([
        {
          product_id: this.data.productId,
          customer_id: this.data.customerId,
          currency_code: this.customForm.get('currency').value,
          hireable_id: this.data.hireableId,
          hireable_type: this.data.hireableType,
          quantity: this.data.quantity ?? 1,
          on_hire: this.data.onHire,
          off_hire: this.data.offHire
        }
      ])
      .pipe(
        mergeMap((prices) => {
          const priceSuggestion = prices[0];
          const priceWithCurrencyFactored =
            this.getPriceFromEstimateWithCurrencyFactor(priceSuggestion);
          return this.hireablePriceUpdateService.update(
            this.data.parentId,
            this.data.hireableId,
            priceWithCurrencyFactored,
            this.pricingType,
            priceSuggestion,
            null,
            false
          );
        })
      );
  }

  private getPriceFromEstimateWithCurrencyFactor(price: PriceSuggestion) {
    // if price does not equal the current currency, assume it is in default currency and multiply by the currency rate
    if (price.estimate.currency_code !== this.data.currency) {
      return price.estimate.price * (this.data?.currencyRate ?? 1);
    }

    // otherwise divide by the currency rate (if using default currency the rate will be 1)
    return price.estimate.price / (this.data?.currencyRate ?? 1);
  }

  private filterUndefined(prices: PriceStructure) {
    const filtered = Object.keys(prices).reduce((acc, key) => {
      if (prices[key] !== undefined && prices[key] !== null) {
        acc[key] = prices[key];
      }
      return acc;
    }, {});

    return filtered;
  }

  private getPricesFromCustomForm() {
    const prices = [
      {
        value: this.customForm.get('first_day').value * 100,
        multiplier: 1,
        unit: 'day',
        sequence: null
      },
      {
        value: this.customForm.get('add_day').value * 100,
        multiplier: 1,
        unit: 'day',
        sequence: '*'
      },
      {
        value: this.customForm.get('week').value * 100,
        multiplier: 1,
        unit: 'week',
        sequence: null
      }
    ];
    if (this.customForm.get('weekend').value) {
      prices.push({
        value: this.customForm.get('weekend').value * 100,
        multiplier: 1,
        unit: 'weekend',
        sequence: null
      });
    }
    if (this.customForm.get('month').value) {
      prices.push({
        value: this.customForm.get('month').value * 100,
        multiplier: 1,
        unit: 'month',
        sequence: null
      });
    }
    return prices;
  }
}
